import { cn } from '@rocco/utils/cn';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useHydrated } from 'remix-utils';
import { useFixedUIHidden } from '~/modules/root';
import { EditModeToggle } from './EditModeToggle';
import { CopyLinkButton } from './CopyLinkButton';
import {
    VisibilityToggleButton,
    type VisibilityItemType,
} from './VisibilityToggleButton';
import makeSharableInternalLink from '../../util/shareable-internal-link';

interface DetailPageButtonsProps {
    /** The ID of the directory item. */
    id: number;
    /** The link to the edit page (Business Manager). */
    editLink: string;
    /** The shareable link (with tracking query params added). */
    shareableLink: string;
    /** The current user has permission to edit this item. */
    canEdit: boolean;
    /** The current user has permission to copy the shareable link. */
    canCopyLink: boolean;
    /** Whether to show the buttons. Defaults to `true`. */
    showButton?: boolean;
    /** When provided, renders a visibility toggle button for admin users. */
    visibilityToggle?: {
        isInDirectory: boolean;
        itemType: VisibilityItemType;
        onToggle: (inLibrary: boolean) => void;
    };
}

/**
 * The "Copy share link" and "Switch to edit mode" buttons on directory item
 * detail pages (professional, product, project, article). Only visible if the
 * current user has the appropriate permissions.
 */
export const DetailPageButtons = ({
    id,
    editLink,
    shareableLink,
    canEdit,
    canCopyLink,
    showButton = true,
    visibilityToggle,
}: DetailPageButtonsProps) => {
    const isHydrated = useHydrated();
    const isFixedUIHidden = useFixedUIHidden();

    const { desktop } = useAppDisplayModeContext();
    if (!desktop) return null;

    const internalLink = isHydrated
        ? makeSharableInternalLink(window.location.origin, shareableLink, id)
        : '';

    return (
        <div
            className={cn(
                'fixed bottom-6 left-8 z-fixed flex flex-col gap-4 transition-[opacity,visibility] duration-300',
                (!showButton || isFixedUIHidden) && 'invisible opacity-0',
            )}
        >
            {visibilityToggle && (
                <VisibilityToggleButton {...visibilityToggle} />
            )}
            {canCopyLink && <CopyLinkButton url={internalLink} />}
            {canEdit && <EditModeToggle editLink={editLink} />}
        </div>
    );
};
